import {
  Callout,
  HStack,
  VStack,
  IconButton,
  Heading,
  Card,
  CardContent,
  Text,
  Flex,
} from "@wootingkb/component-library";
import { BsArrowRight } from "react-icons/bs";
import NextLink from "next/link";

// Custom components / utils
import { CardItem, CardItemContainer } from "../components/Common/CardItem";
import PageTitle from "../components/Common/PageTitle";
import OrderItem from "../components/Common/OrderItem";
import OrderHistory from "../components/OrderHistory";
import useWootingUser from "../utils/hooks/useWootingUser";

// Hooks
import { useOrders } from "../utils/hooks/useOrders";
import RecommendedArticles from "../components/Common/RecommendedArticles";
import { useHolidayToast } from "../utils/showToast";
import { msg } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react";
import { MdInfoOutline } from "react-icons/md";
import { ReplaceableKey } from "../lib/helper/stringReplacer";
import {
  RegionalInstanceId,
  useRegionalInstanceContext,
} from "../lib/[services]/regional-instance";

const VISIT_PROFILE_PAGE_ARIA_LABEL = msg({
  id: "Home_visit_profile_page_aria",
  message: "visit profile page",
});

interface AIProps {
  children: React.ReactNode;
  href?: string;
}

function AlertItem({ children, href }: AIProps) {
  const { _: i18n } = useLingui();

  return (
    <Callout
      status="warning"
      textContainerClassName="w-full"
      heading={
        <HStack className="w-full items-center justify-between gap-2">
          <HStack className="items-center gap-2">
            <MdInfoOutline
              size={24}
              className="text-foreground-brand-secondary"
            />
            {children}
          </HStack>
          {href && (
            <NextLink href={href}>
              <IconButton
                aria-label={i18n(VISIT_PROFILE_PAGE_ARIA_LABEL)}
                size="sm"
              >
                <BsArrowRight />
              </IconButton>
            </NextLink>
          )}
        </HStack>
      }
    />
  );
}

const YOUR_ORDERS_TEXT = msg({
  id: "Home_your_orders_text",
  message: "Your Orders",
});

const UPDATES_BY_US_TEXT = msg({
  id: "Home_updates_by_us_text",
  message: "Updates by us",
});

const CONNECT_DISCORD_ALERT_TEXT = msg({
  id: "Home_connect_discord_alert_text",
  message: "Connect your discord & claim your role!",
});

const PREORDER_60HE_V2_TEXT = msg({
  id: "Home_preorder_60he_v2_text",
  message: "Pre-order today: Wooting 60HE v2",
});

const WOOTILITY_V5_TEXT = msg({
  id: "Home_wootility_v5_text",
  message: "Wootility v5 is here!",
});

export default function Index() {
  const { orders, hasOrders } = useOrders();
  const [wootingUser] = useWootingUser();
  const latestOrder = orders && orders[0];
  const { first_name } = wootingUser?.customer_name || {};
  const { id } = useRegionalInstanceContext();
  const { _: i18n } = useLingui();

  useHolidayToast();

  return (
    <Flex className="2xl:flex-row 2xl:gap-16">
      <VStack>
        <PageTitle
          className="rb-mask"
          title={
            first_name ? (
              <Trans id="Home_welcome_message_with_name">
                Hey, {first_name}
              </Trans>
            ) : (
              <Trans id="Home_welcome_message_without_name">Hey!</Trans>
            )
          }
        />
        {/* Alerts */}
        {first_name &&
          !wootingUser?.hasLinkedDiscord &&
          id !== RegionalInstanceId.CN && (
            <VStack className="mt-4 mb-8 gap-4">
              {/* <AlertItem
                text="There seems to be an issue with your address"
                href="/order/view"
              /> */}
              <AlertItem href="/my/account">
                {i18n(CONNECT_DISCORD_ALERT_TEXT)}
              </AlertItem>
            </VStack>
          )}

        {/* Order item */}
        {hasOrders ? (
          <VStack className="gap-4">
            <Heading as="h3" size="h4" weight="semibold">
              <Trans id="Home_your_latest_order">Your latest order</Trans>
            </Heading>
            <OrderItem order={orders ? orders[0] : undefined} />
          </VStack>
        ) : (
          <Card>
            <CardContent>
              <Text weight="bold">
                <Trans id="Home_no_orders_in_account">
                  There are no orders in your account.
                </Trans>
              </Text>
              <Text>
                <Trans id="Home_go_to_orders">
                  Go to{" "}
                  <NextLink href="/my/orders" className="italic underline">
                    your orders
                  </NextLink>{" "}
                  to transfer orders from another account.
                </Trans>
              </Text>
            </CardContent>
          </Card>
        )}

        {/* Recommended articles */}
        {latestOrder && <RecommendedArticles order={latestOrder} />}

        {/* Update cards */}
        <CardItemContainer title={i18n(UPDATES_BY_US_TEXT)}>
          <CardItem
            imgSrc={"/images/60he-v2_table_split.webp"}
            href={`${ReplaceableKey.WootingStoreURL}/wooting-60he-v2`}
            title={i18n(PREORDER_60HE_V2_TEXT)}
          />
          <CardItem
            imgSrc={"/images/wootility.webp"}
            href={`${ReplaceableKey.WootingStoreURL}/wootility`}
            title={i18n(WOOTILITY_V5_TEXT)}
          />
        </CardItemContainer>
      </VStack>
      <VStack className="mt-10 gap-4 2xl:mt-0">
        <PageTitle title={i18n(YOUR_ORDERS_TEXT)} />
        <OrderHistory limitWeight />
      </VStack>
    </Flex>
  );
}
